// @flow
/**
 * A component that, based on the module and message parameters, prepares content
 * for the BotMessage component.
 */

import React, { createContext } from "react";
import { useStore } from "react-redux";
import { isEmpty } from "ramda";

import { MessageTypes, ModuleRoles, ModuleTypes, MessageContextType } from "../../lib/types";
import PricingComponent from "../PricingModule/PricingModule";
import FinancialResults from "./FinancialResults";
import { IAaction, IAMessageContent } from "../InstantAnswers/Messages";
import LocationsComponent from "../Locations/Locations";
import { Feedback } from "../InstantAnswers/Feedback";
import { personalizeMessage } from "./utils";
import { getUserFirstName } from "./selectors";
import { getActiveModule, getDisableMobile, getLeadId, getModuleById, getModulesByName } from "../../store";
import { MultiAnswers } from "./MultiAnswers";
import BotMessage from "./BotMessage";
import { isMobileUserAgent, replaceCustomVariables } from "../../lib/utils";
import { ExtraAnswersGenerator } from "./ExtraAnswersGenerator";
import { InChatGalleryStylesWrapper, GalleryInner } from "../Gallery/GalleryInner";

import { FloorplansGallery } from "../Gallery/FloorplansGallery";
import type { RichModule } from "../../lib/types";

export const MessageContext = createContext<MessageContextType>({
  isWelcomeBackMessage: false,
  isFirstModule: false,
  isPricing: false,
  isLocations: false,
  isErrorMessage: false,
  isFeedback: false,
  messageNum: null,
  messageType: null,
  fromHistory: false,
  messageData: null,
  module: null,
  customMobilePreview: false,
});

type Props = {
  isLastMessage: boolean,
  messageNum: number,
  key: any,
  messageData: any,
};

/**
 * Defines content for the bot message and the context for the component down the tree.
 * @param props
 * @return {*}
 * @constructor
 */
export const BotMessageGenerator = (props: Props) => {
  const { isLastMessage, messageNum, messageData } = props;

  const store = useStore().getState();

  const { type: messageType, answers: storedAnswers, data: iaData, locationsData, fromHistory } = messageData;

  let module: RichModule = getModuleById(store, messageData.id);
  const isLocations = messageType === MessageTypes.LOCATIONS_MESSAGE;

  if (isLocations) {
    module = getActiveModule(store);
  }
  // TODO: That's some unhandy shit here - need to add to this list every time I add a new message type
  if (
    [MessageTypes.EXTRA_BOT_MESSAGE, MessageTypes.IA_BOT_MESSAGE, MessageTypes.ERROR_MESSAGE].includes(messageData.type)
  ) {
    module.text = messageData.text;
  }

  const answers = module.answers || storedAnswers || []; // Oh God, that's dirty.

  const { id: moduleID, text: moduleText, role, type: moduleType } = module;

  const messageClasses = [];

  let result: {
    messageClass: string,
    Actions?: any,
    BubbleContent?: any,
    RawContent?: any,
  } = {
    messageClass: "",
  };

  const isIa = [MessageTypes.IA_BOT_MESSAGE, MessageTypes.IA_USER_MESSAGE].includes(messageType);
  const isFinancialResults = moduleType === ModuleTypes.financialResults;
  const isPricing = role === ModuleRoles.pricing;
  const isInChatGallery = module.isInChatGallery();
  const isInChatDigitalTour = module.isInChatDigitalTour();
  const isInChatFloorplan = module.isInChatFloorplan();
  const locationsExist = locationsData?.length > 0;
  const isFeedback = messageType === MessageTypes.IA_FEEDBACK_MESSAGE;
  const isErrorMessage = messageType === MessageTypes.ERROR_MESSAGE;
  const isFirstModule = moduleID === 1;
  const isWelcomeBackMessage =
    isFirstModule && getLeadId(store) && getUserFirstName(store) && !isEmpty(getModulesByName(store));
  const isMultiOption = moduleType === ModuleTypes.multiOption;
  const showAnswers = [ModuleTypes.slack, ModuleTypes.option].includes(moduleType) || isLocations || isErrorMessage;
  const customMobilePreview = isFirstModule && getDisableMobile(store) && isMobileUserAgent;

  const text = replaceCustomVariables(personalizeMessage(moduleText, getUserFirstName(store), isWelcomeBackMessage));

  if (isPricing) {
    result = {
      ...result,
      RawContent: <PricingComponent text={text} />,
    };
  } else if (isFinancialResults) {
    result = {
      ...result,
      RawContent: <FinancialResults action={module?.action} />,
    };
  } else if (isInChatGallery || isInChatDigitalTour) {
    result = {
      ...result,
      RawContent: (
        <InChatGalleryStylesWrapper>
          <GalleryInner galleryModule={module} />
        </InChatGalleryStylesWrapper>
      ),
    };
  } else if (isInChatFloorplan) {
    result = {
      ...result,
      RawContent: (
        <InChatGalleryStylesWrapper>
          <FloorplansGallery galleryModule={module} />
        </InChatGalleryStylesWrapper>
      ),
    };
  } else if (isIa) {
    const iaAction = iaData?.action;

    if (iaData.meta?.hasFeedbackPrompt) {
      messageClasses.push("has-feedback");
    }

    result = {
      ...result,
      BubbleContent: <IAMessageContent values={iaData.values} />,
      Actions: iaAction ? <IAaction action={iaAction} /> : null,
    };
  } else if (isLocations) {
    if (locationsExist) {
      result = {
        ...result,
        RawContent: <LocationsComponent locationsData={locationsData} />,
      };
    } else {
      result = {
        ...result,
        BubbleContent: text,
      };
    }
  } else if (isFeedback) {
    if (!isLastMessage) {
      return null;
    }

    result = {
      ...result,
      RawContent: <Feedback data={iaData} />,
    };
  } else {
    result = {
      ...result,
      BubbleContent: <div className="text">{text}</div>,
    };
  }

  if (customMobilePreview) {
    result.BubbleContent = null;
  }

  if (isMultiOption) {
    messageClasses.push("multi-option");

    result.Actions = <MultiAnswers answers={answers} />;
  }
  if (showAnswers) {
    result.Actions = <ExtraAnswersGenerator answers={answers} />;
  }
  if (isLastMessage) {
    messageClasses.push("last-message");
  }
  if (fromHistory) {
    messageClasses.push("from-history");
  }
  result.messageClass = messageClasses.join(" ");

  return (
    // TODO: if I'm not mistaken - this has to be created somewhere else
    // in order to prevent extra re-rendering every time because of a new object.
    <MessageContext.Provider
      value={{
        isWelcomeBackMessage,
        isFirstModule,
        isPricing,
        isLocations,
        isErrorMessage,
        isLastMessage,
        isFeedback,
        messageNum,
        messageType,
        fromHistory,
        messageData,
        module,
        customMobilePreview,
      }}
    >
      <BotMessage {...result} />
    </MessageContext.Provider>
  );
};
